import { Skeleton } from '@zyxantech/tux'
import { lazy, Suspense, type ComponentType } from 'react'

/**
 * The builder's entry points into the app — and the code-split boundary.
 *
 * The router imports THIS file and nothing else from `@/builder`: a static
 * barrel would drag the runtime (registry, resolvers, canvas, and from Stage 5
 * the VectorCanvas) back into the main bundle and undo the split. A deployment
 * whose users never open a dashboard never downloads any of it.
 */

const BuilderModule = lazy(() => import('./BuilderModule'))
const DashboardsPage = lazy(async () => ({
  default: (await import('./pages/DashboardsPage')).DashboardsPage,
}))
const BuilderPage = lazy(async () => ({
  default: (await import('./pages/BuilderPage')).BuilderPage,
}))
const ViewerPage = lazy(async () => ({
  default: (await import('./pages/ViewerPage')).ViewerPage,
}))
const ConnectorsPage = lazy(async () => ({
  default: (await import('./pages/ConnectorsPage')).ConnectorsPage,
}))
const SavedQueriesPage = lazy(async () => ({
  default: (await import('./pages/SavedQueriesPage')).SavedQueriesPage,
}))
const QueryEditorPage = lazy(async () => ({
  default: (await import('./pages/QueryEditorPage')).QueryEditorPage,
}))

function lazyRoute(Component: ComponentType) {
  return function LazyRoute() {
    return (
      <Suspense fallback={<Skeleton height={320} />}>
        <Component />
      </Suspense>
    )
  }
}

/** The dashboard list — the entry point for viewers and authors alike. */
export const DashboardsRoute = lazyRoute(DashboardsPage)

/** The author's builder: palette, canvas, config and binding panels. */
export const DashboardBuilderRoute = lazyRoute(BuilderPage)

/** The published, read-only view. */
export const DashboardViewerRoute = lazyRoute(ViewerPage)

/** Admin authoring surfaces (Stage 2.5): connections and stored queries. */
export const ConnectorsRoute = lazyRoute(ConnectorsPage)
export const SavedQueriesRoute = lazyRoute(SavedQueriesPage)
export const QueryEditorRoute = lazyRoute(QueryEditorPage)

/** The runtime status page (Stage 0/1 diagnostics for the builder module). */
export const BuilderRoute = lazyRoute(BuilderModule)
